import { useEffect, useRef, useState } from 'react';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//styles
import {
  Scene,
  Viz,
  Atmo,
  Blob1,
  Blob2,
  Blob3,
  SceneInner,
  Frame,
  FrameTop,
  FrameTitle,
  Chips,
  Chip,
  ChipDot,
  ChipTime,
} from './HeroVisual.styles';

const EVENT_POOL = [
  { text: 'Tender accepted &middot; ATL &#8594; DFW', time: '&middot; just now' },
  {
    text: 'Exception closed by a Freight Expert',
    time: '&middot; 4 min ago',
    human: true,
  },
  { text: 'Status synced to your ERP', time: '&middot; 1 min ago' },
  { text: 'Dock appointment confirmed', time: '&middot; 2 min ago' },
  { text: 'Invoice audited &middot; $4,120', time: '&middot; 6 min ago' },
  {
    text: 'Spot rate negotiated &middot; MEM &#8594; ORD',
    time: '&middot; 3 min ago',
  },
  { text: 'Driver checked in &middot; gate 3', time: '&middot; just now' },
  {
    text: 'Detention dispute settled &middot; $375',
    time: '&middot; 8 min ago',
    human: true,
  },
];

const CHIP_POSITIONS = [
  { top: '18%', left: '-5%' },
  { top: '52%', right: '-5%' },
  { bottom: '9%', left: '1%' },
];

const FRAME_SVG = `<svg viewBox="0 54 560 367" role="img" aria-label="Isometric freight yard: an extruded warehouse with dock doors, trailers parked in the yard, a gate with an approaching truck, and a route weaving from the gate through the yard to an active dock door.">
  <defs>
    <linearGradient id="lsVizGrad-home" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#00D7D7"/><stop offset="1" stop-color="#14D64D"/></linearGradient>
    <mask id="lsVizMask-home"><polygon points="300.0,108.0 580.6,270.0 346.8,405.0 66.2,243.0" fill="white"/></mask>
  </defs>
  <g mask="url(#lsVizMask-home)"><g stroke="rgba(252,252,255,.05)" stroke-width="1"><line x1="300.0" y1="108.0" x2="66.2" y2="243.0"/><line x1="323.4" y1="121.5" x2="89.6" y2="256.5"/><line x1="346.8" y1="135.0" x2="112.9" y2="270.0"/><line x1="370.1" y1="148.5" x2="136.3" y2="283.5"/><line x1="393.5" y1="162.0" x2="159.7" y2="297.0"/><line x1="416.9" y1="175.5" x2="183.1" y2="310.5"/><line x1="440.3" y1="189.0" x2="206.5" y2="324.0"/><line x1="463.7" y1="202.5" x2="229.9" y2="337.5"/><line x1="487.1" y1="216.0" x2="253.2" y2="351.0"/><line x1="510.4" y1="229.5" x2="276.6" y2="364.5"/><line x1="533.8" y1="243.0" x2="300.0" y2="378.0"/><line x1="557.2" y1="256.5" x2="323.4" y2="391.5"/><line x1="580.6" y1="270.0" x2="346.8" y2="405.0"/><line x1="300.0" y1="108.0" x2="580.6" y2="270.0"/><line x1="276.6" y1="121.5" x2="557.2" y2="283.5"/><line x1="253.2" y1="135.0" x2="533.8" y2="297.0"/><line x1="229.9" y1="148.5" x2="510.4" y2="310.5"/><line x1="206.5" y1="162.0" x2="487.1" y2="324.0"/><line x1="183.1" y1="175.5" x2="463.7" y2="337.5"/><line x1="159.7" y1="189.0" x2="440.3" y2="351.0"/><line x1="136.3" y1="202.5" x2="416.9" y2="364.5"/><line x1="112.9" y1="216.0" x2="393.5" y2="378.0"/><line x1="89.6" y1="229.5" x2="370.1" y2="391.5"/><line x1="66.2" y1="243.0" x2="346.8" y2="405.0"/></g></g>
  <path d="M356.1 345.1 Q379.5 315.4 380.7 302.5 Q381.8 289.7 386.5 270.8 Q391.2 251.9 367.2 253.6 Q343.3 255.3 333.3 249.5 T323.4 243.8 " fill="none" stroke="url(#lsVizGrad-home)" stroke-width="2" stroke-dasharray="4 7" stroke-linecap="round" opacity=".7"/>
  <g stroke="rgba(252,252,255,.24)" stroke-width="1.3" stroke-linejoin="round"><polygon points="122.3,162.0 239.2,229.5 239.2,288.9 122.3,221.4" fill="rgba(252,252,255,.045)"/><polygon points="379.5,148.5 239.2,229.5 239.2,288.9 379.5,207.9" fill="rgba(252,252,255,.075)"/><polygon points="262.6,81.0 379.5,148.5 239.2,229.5 122.3,162.0" fill="rgba(252,252,255,.11)"/></g>
  <g stroke="rgba(252,252,255,.30)" stroke-width="1"><polygon points="360.8,179.5 340.9,191.0 340.9,230.2 360.8,218.7" fill="rgba(252,252,255,.06)"/><polygon points="329.2,197.8 309.4,209.2 309.4,248.4 329.2,236.9" fill="rgba(252,252,255,.06)"/><polygon points="297.7,216.0 277.8,227.5 277.8,266.6 297.7,255.1" fill="rgba(252,252,255,.06)"/></g>
  <polygon points="329.2,197.8 309.4,209.2 309.4,248.4 329.2,236.9" fill="rgba(0,214,214,.12)" stroke="url(#lsVizGrad-home)" stroke-width="2"/>
  <g stroke="rgba(252,252,255,.24)" stroke-width="1.1" stroke-linejoin="round"><polygon points="428.6,211.9 480.0,241.7 480.0,263.2 428.6,233.5" fill="rgba(252,252,255,.05)"/><polygon points="501.1,229.5 480.0,241.7 480.0,263.2 501.1,251.1" fill="rgba(252,252,255,.09)"/><polygon points="449.6,199.8 501.1,229.5 480.0,241.7 428.6,211.9" fill="rgba(252,252,255,.13)"/></g>
  <g stroke="rgba(252,252,255,.24)" stroke-width="1.1" stroke-linejoin="round"><polygon points="442.6,257.8 494.1,287.6 494.1,309.1 442.6,279.4" fill="rgba(252,252,255,.05)"/><polygon points="515.1,275.4 494.1,287.6 494.1,309.1 515.1,297.0" fill="rgba(252,252,255,.09)"/><polygon points="463.7,245.7 515.1,275.4 494.1,287.6 442.6,257.8" fill="rgba(252,252,255,.13)"/></g>
  <g stroke="rgba(252,252,255,.24)" stroke-width="1.1" stroke-linejoin="round"><polygon points="367.8,252.4 419.3,282.1 419.3,303.8 367.8,274.0" fill="rgba(252,252,255,.05)"/><polygon points="440.3,270.0 419.3,282.1 419.3,303.8 440.3,291.6" fill="rgba(252,252,255,.09)"/><polygon points="388.9,240.3 440.3,270.0 419.3,282.1 367.8,252.4" fill="rgba(252,252,255,.13)"/></g>
  <g stroke="rgba(252,252,255,.24)" stroke-width="1.1" stroke-linejoin="round"><polygon points="225.2,305.1 260.2,325.3 260.2,344.2 225.2,324.0" fill="rgba(252,252,255,.05)"/><polygon points="281.3,313.2 260.2,325.3 260.2,344.2 281.3,332.1" fill="rgba(252,252,255,.10)"/><polygon points="246.2,292.9 281.3,313.2 260.2,325.3 225.2,305.1" fill="rgba(252,252,255,.14)"/></g><g stroke="rgba(252,252,255,.24)" stroke-width="1.1" stroke-linejoin="round"><polygon points="261.4,319.3 276.6,328.0 276.6,352.3 261.4,343.6" fill="rgba(252,252,255,.06)"/><polygon points="295.3,317.2 276.6,328.0 276.6,352.3 295.3,341.5" fill="rgba(252,252,255,.11)"/><polygon points="280.1,308.5 295.3,317.2 276.6,328.0 261.4,319.3" fill="rgba(252,252,255,.16)"/></g>
  <g stroke="rgba(252,252,255,.24)" stroke-width="1" stroke-linejoin="round"><polygon points="332.3,295.9 339.8,300.2 339.8,342.1 332.3,337.8" fill="rgba(252,252,255,.05)"/><polygon points="347.2,295.9 339.8,300.2 339.8,342.1 347.2,337.8" fill="rgba(252,252,255,.09)"/><polygon points="339.8,291.6 347.2,295.9 339.8,300.2 332.3,295.9" fill="rgba(252,252,255,.12)"/></g><g stroke="rgba(252,252,255,.24)" stroke-width="1" stroke-linejoin="round"><polygon points="376.7,321.6 384.2,325.9 384.2,367.7 376.7,363.4" fill="rgba(252,252,255,.05)"/><polygon points="391.7,321.6 384.2,325.9 384.2,367.7 391.7,363.4" fill="rgba(252,252,255,.09)"/><polygon points="384.2,317.2 391.7,321.6 384.2,325.9 376.7,321.6" fill="rgba(252,252,255,.12)"/></g><g stroke="none" stroke-width="0" stroke-linejoin="round" opacity="0.9"><polygon points="342.1,305.1 379.5,326.7 379.5,329.9 342.1,308.3" fill="url(#lsVizGrad-home)"/><polygon points="381.8,325.3 379.5,326.7 379.5,329.9 381.8,328.6" fill="url(#lsVizGrad-home)"/><polygon points="344.4,303.8 381.8,325.3 379.5,326.7 342.1,305.1" fill="url(#lsVizGrad-home)"/></g>
  <circle r="3.6" fill="#00D7D7"><animateMotion dur="4.2s" repeatCount="indefinite" path="M356.1 345.1 Q379.5 315.4 380.7 302.5 Q381.8 289.7 386.5 270.8 Q391.2 251.9 367.2 253.6 Q343.3 255.3 333.3 249.5 T323.4 243.8 "/></circle><circle r="3.2" fill="#14D64D"><animateMotion dur="4.2s" begin="-2.1s" repeatCount="indefinite" path="M356.1 345.1 Q379.5 315.4 380.7 302.5 Q381.8 289.7 386.5 270.8 Q391.2 251.9 367.2 253.6 Q343.3 255.3 333.3 249.5 T323.4 243.8 "/></circle>
  <text x="291.3" y="69.8" text-anchor="middle" font-family="Manrope,system-ui,sans-serif" font-size="10.5" font-weight="700" letter-spacing="2.5" fill="#6C8583">DOCK</text><text x="505" y="170" text-anchor="middle" font-family="Manrope,system-ui,sans-serif" font-size="10.5" font-weight="700" letter-spacing="2.5" fill="#6C8583">YARD</text><text x="373.5" y="394.1" text-anchor="middle" font-family="Manrope,system-ui,sans-serif" font-size="10.5" font-weight="700" letter-spacing="2.5" fill="#6C8583">GATE</text><text x="110" y="405" text-anchor="start" font-family="Manrope,system-ui,sans-serif" font-size="9.5" font-weight="700" letter-spacing="1.5" fill="#6C8583">TMS &#183; PLANNED &amp; TENDERED</text>
</svg>`;

export function HeroVisual() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const sceneRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [chipStates, setChipStates] = useState(() =>
    CHIP_POSITIONS.map((_, i) => ({ idx: i, show: false })),
  );

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const el = sceneRef.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => setPaused(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const inUse = new Set(CHIP_POSITIONS.map((_, i) => i));
    const intervals = [];
    const timeouts = [];

    function nextFree(from) {
      for (let s = 1; s <= EVENT_POOL.length; s++) {
        const c = (from + s) % EVENT_POOL.length;
        if (!inUse.has(c)) return c;
      }
      return from;
    }

    CHIP_POSITIONS.forEach((_, i) => {
      timeouts.push(
        setTimeout(
          () => {
            if (cancelled) return;
            setChipStates((prev) => {
              const next = [...prev];
              next[i] = { ...next[i], show: true };
              return next;
            });
          },
          prefersReducedMotion ? 0 : 260 * i,
        ),
      );

      if (prefersReducedMotion) return;

      const period = 3200 + i * 820;
      intervals.push(
        setInterval(() => {
          if (cancelled) return;
          setChipStates((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], show: false };
            return next;
          });
          timeouts.push(
            setTimeout(() => {
              if (cancelled) return;
              setChipStates((prev) => {
                const currentIdx = prev[i].idx;
                inUse.delete(currentIdx);
                const newIdx = nextFree(currentIdx + i);
                inUse.add(newIdx);
                const next = [...prev];
                next[i] = { idx: newIdx, show: true };
                return next;
              });
            }, 480),
          );
        }, period),
      );
    });

    return () => {
      cancelled = true;
      intervals.forEach(clearInterval);
      timeouts.forEach(clearTimeout);
    };
  }, [prefersReducedMotion]);

  return (
    <Scene ref={sceneRef}>
      <Viz>
        <Atmo aria-hidden="true">
          <Blob1 $paused={paused} />
          <Blob2 $paused={paused} />
          <Blob3 $paused={paused} />
        </Atmo>
        <SceneInner>
          <Frame>
            <FrameTop>
              <FrameTitle>Your network, running itself</FrameTitle>
            </FrameTop>
            <div dangerouslySetInnerHTML={{ __html: FRAME_SVG }} />
          </Frame>
          <Chips>
            {chipStates.map((chip, i) => {
              const event = EVENT_POOL[chip.idx];
              return (
                <Chip key={i} style={CHIP_POSITIONS[i]} $show={chip.show}>
                  <ChipDot $human={event.human} />
                  <span dangerouslySetInnerHTML={{ __html: event.text }} />
                  <ChipTime dangerouslySetInnerHTML={{ __html: event.time }} />
                </Chip>
              );
            })}
          </Chips>
        </SceneInner>
      </Viz>
    </Scene>
  );
}
